import Button from "react-bootstrap/Button"
import Tab from "react-bootstrap/Tab"
import Table from "react-bootstrap/Table"
import Tabs from "react-bootstrap/Tabs"
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Alert from "react-bootstrap/Alert"
import "./cache-view.css"
import { useEffect, useState } from "react"
import request, { post } from "../../internal/request"
import { API_ADDR, Hotel as ListHotel } from "../../App"
import InfoMessage from "../info-message/info-message"
import PruneOldVersions from "../prune-old-versions/prune-old-versions";
import Rollback from "../rollback-version/rollback";

interface Props {
  runtimeEnv: string
  brandCode: string
  hotelReferenceId: string
  hotel: ListHotel | undefined
  reRender: string
}

interface cacheStateTable {
  environment: string
  cacheState: string[][]
  canPrune: boolean
  canRollback: boolean
  numVersions: number
}

export interface Diff {
  environment: string
  status: string
  differences: DiffOutput[]
}

export interface DiffOutput {
  endpoint: string
  difference: string
}

enum cacheDataCol {
  minVersion = 0,
  maxVersion,
  versionCount,
  currentVersion
}

export default function CacheView(props: Props) {

  //const [runtimeEnv, setRuntimeEnv] = useState(props.runtimeEnv)
  const [envCacheTables, setEnvCacheTables] = useState<cacheStateTable[]>()
  const [getDiff, setDiff] = useState<Diff[]>()
  const [loading, setLoading] = useState(false)
  const [showEnvironment, setShowEnvironment] = useState("")
  const [reload, setReload] = useState(0)
  const [publishing, setPublishing] = useState(false)

  // Modal props
  const [showModal, setShowModal] = useState("")
  const [modalTitle, setModalTitle] = useState("")
  const [modalText, setModalText] = useState("")
  const [minVersionCount, setMinVersionCount] = useState(0)
  const [maxVersionCount, setMaxVersionCount] = useState(0)

  // Set the initial env when the runtime env changes (i.e. at startup)
  useEffect(() => {
    setShowEnvironment(props.runtimeEnv)
  }, [props.runtimeEnv])

  useEffect(() => {

    setLoading(true)
    setPublishing(false)  // Clear publishing button lockout

    if (props.hotelReferenceId !== "") {

      (async (hotelReferenceId) => {
        await request<cacheStateTable[]>(`${API_ADDR()}/api/cachestate?hotelreferenceid=${hotelReferenceId}`)
          .then((res) => setEnvCacheTables(res))
          .catch((error) => console.log(`Error getting cache state for ${hotelReferenceId}`, error))

        await request<Diff[]>(`${API_ADDR()}/api/diff?hotelreferenceid=${hotelReferenceId}`)
          .then((diff) => setDiff(diff))
          .catch((error) => console.log(`Error getting diffs for ${hotelReferenceId}`, error))

        setLoading(false)
      })(props.hotelReferenceId)
    } else {
      setLoading(false)
    }
  }, [props.hotelReferenceId, props.reRender, reload])

  function publishHotel(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, hotelReferenceId: string, environment: string): boolean {

    setPublishing(true)

    // Call publish
    post(`${API_ADDR()}/api/publish?hotelreferenceid=${hotelReferenceId}&environment=${environment}`)
      .then((res) => {
        if (res.status === 204) {
          // All good: Publish succeeded. Refresh somehow.
          setReload(reload + 1)
        } else {
          console.log("Publish did not succeed, response: ", res.json())
        }
      })
      .catch((error) => {
        console.log("Publish failed with error: ", error)
      })

    return true
  }

  // #region Display Helpers
  interface cacheItemType {
    id: number
    data: string[]
  }
  const CacheItem = ({ id, data }: cacheItemType) => (
    <OverlayTrigger overlay={<Tooltip id={id.toString()}>
      <ul>
        <li>Oldest Version: v{data[cacheDataCol.minVersion]}</li>
        <li>Newest Version: v{data[cacheDataCol.maxVersion]}</li>
        <li>Current Version: v{data[cacheDataCol.currentVersion]}</li>
        <li>Versions in DB: {data[cacheDataCol.versionCount]}</li>
      </ul>
    </Tooltip>} placement="bottom">
      <p className={data[cacheDataCol.currentVersion] !== data[cacheDataCol.maxVersion] ? "highlight" : undefined}>v{data[cacheDataCol.currentVersion]}
        {data[cacheDataCol.currentVersion] !== data[cacheDataCol.maxVersion] ? ` (of ${data[cacheDataCol.maxVersion]})` : null}
      </p>
    </OverlayTrigger>
  );

  function renderEnvCacheTable(environment: string): JSX.Element {

    if (loading) {
      return (
        <>
          <div className="subheader" style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <div>Cache State</div>
          </div>
          <div style={{ padding: 10 }}>Loading cache state...</div>
        </>
      )
    }

    const cacheState = envCacheTables?.find((ct) => ct.environment === environment)
    const cacheData = cacheState?.cacheState ?? []

    if (cacheData.length === 0) {
      return (
        <>
          <div className="subheader" style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <div>Cache State</div>
          </div>
          <div style={{ padding: 10 }}>Not cached in this environment.</div>
        </>
      )
    }
    const cols = cacheData[0]
    const data = cacheData.slice(1)

    return (
      <>
        <div className="subheader" style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <div>Cache State</div>
          <div>
            <Button variant={cacheState?.canPrune ?? false ? "primary" : "outline-primary"} disabled={!cacheState?.canPrune ?? true} style={{ marginLeft: 2, marginRight: 2 }} onClick={() => cacheFunc("prune", calcKeepMin(data), calcKeepMax(data))} >Prune Old Versions</Button>
            <Button variant={cacheState?.canRollback ?? false ? "primary" : "outline-primary"} disabled={!cacheState?.canRollback ?? true} style={{ marginLeft: 2, marginRight: 2 }} onClick={() => cacheFunc("rollback", calcRollbackMax(data), 0)} >Rollback</Button>
          </div>
        </div>
        <div style={{ width: "calc(100% - 15px)" }}>
          <Table striped="columns" bordered className="vtable">
            <thead>
              <tr key="head">
                {cols!.map((col, cidx) => {
                  return (<th key={`head-${cidx}`} style={{ textAlign: "center" }}>{col}</th>)
                })}
              </tr>
            </thead>
            <tbody>
              {
                data.map((row, idx) => {
                  return (<tr key={idx}>
                    {row.map((col, cidx) => {
                      return <td key={`${idx}-${cidx}`} style={cidx !== 0 ? { textAlign: "center" } : {}} >
                        {cidx === 0 ? col : <CacheItem id={idx} data={col.split(",")} />}
                      </td>
                    })}

                  </tr>)
                })
              }
            </tbody>
          </Table>
        </div>
      </>
    )
  }
  function renderEnvDiff(environment: string) {

    const envDiff = getDiff?.find((d) => d.environment === environment)

    let content = <></>

    // Depending on Diff object, return appropriate message(s)
    if (loading || envDiff === undefined)
      content = <div style={{ padding: 10 }}>Checking diff...</div>
    else if (envDiff.status === "NOT_AVAILABLE")
      content = <div style={{ padding: 10 }}>This hotel is not available in {showEnvironment}</div>
    else if (envDiff.status === "NOT_CACHED")
      content = <div style={{ padding: 10 }}>This hotel is not cached in {showEnvironment}</div>
    else if (envDiff.status === "SAME")
      content = <div style={{ padding: 10 }}>No pending changes.</div>
    else {
      // Ensure we only return unique differences, to reduce cognitive load & screen clutter      
      const uniqueDiffs = [...new Set(envDiff.differences.map((diff) => `${diff.endpoint} - ${diff.difference}`))];
      content = (
        <div style={{ padding: 10 }}>
          <ul>
            {uniqueDiffs.sort((a, b) => a.toLowerCase() === b.toLowerCase() ? 0 : a.toLowerCase() < b.toLowerCase() ? -1 : 1).map((diff, idx) => {
              return <li key={idx}>{diff}</li>
            })}
          </ul>
        </div>
      )
    }

    const disablePublish = publishing || loading || !(envDiff?.status === "DIFFERENT" || envDiff?.status === "NOT_CACHED")
    const disableRefresh = publishing || loading

    return (
      <>
        <div className="subheader topsep" style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <div>Pending changes</div>
          <div>
            <Button
              variant={disablePublish ? "outline-primary" : "primary"}
              disabled={disablePublish}
              onClick={(e) => publishHotel(e, props.hotelReferenceId, showEnvironment)}
              style={{ marginRight: 5 }}
            >
              {publishing
                ? <><img src={process.env.PUBLIC_URL + "/loading-spin.svg"} alt="publish spinner" /> Publishing {showEnvironment}...</>
                : <>Publish {showEnvironment}</>
              }
            </Button>
            <Button
              variant={disableRefresh ? "outline-primary" : "primary"}
              disabled={disableRefresh}
              onClick={() => setReload(reload + 1)}
            >
              <img width={20} src={process.env.PUBLIC_URL + "/refresh.svg"} alt="refresh view" />
            </Button>
          </div>
        </div>
        {content}
      </>
    )

  }
  function prettyVisibility(visibility: string): string {
    switch (visibility) {
      case "obsolete":
        return "localhost only (scheduled for deletion)"
      case "hidden":
        return "development environment only"
      case "preview":
        return "development & staging environments"
      case "live":
        return "all environments"
      default:
        return "unknown environments"
    }
  }
  // #endregion

  function calcRollbackMax(data: string[][]): number {

    let rollbackVers = 999  // It should never get this high... hopefully!
    for (let i = 0; i < data.length; i++) {
      for (let ii = 0; ii < data[i].length; ii++) {
        const elem = data[i][ii].split(",")
        if (+elem[cacheDataCol.currentVersion] - +elem[cacheDataCol.minVersion] < rollbackVers)
          rollbackVers = +elem[cacheDataCol.currentVersion] - +elem[cacheDataCol.minVersion]
      }
    }

    if (rollbackVers === 999)
      return 0

    return rollbackVers

  }
  function calcKeepMin(data: string[][]): number {
    // Determine the smallest number of versions to retain when pruning
    let keepMin = 999 // Should never be this high!
    for (let i = 0; i < data.length; i++) {
      for (let ii = 0; ii < data[i].length; ii++) {
        const elem = data[i][ii].split(",")
        if (+elem[cacheDataCol.maxVersion] - +elem[cacheDataCol.currentVersion] < keepMin)
          keepMin = +elem[cacheDataCol.maxVersion] - +elem[cacheDataCol.currentVersion]
      }
    }

    if (keepMin === 999)
      return 0

    return keepMin + 1

  }
  function calcKeepMax(data: string[][]): number {
    // Determine the largest number of versions to retain when pruning
    let keepMax = 999 // Should never be this high!
    for (let i = 0; i < data.length; i++) {
      for (let ii = 0; ii < data[i].length; ii++) {
        const elem = data[i][ii].split(",")
        if (+elem[cacheDataCol.versionCount] < keepMax)
          keepMax = +elem[cacheDataCol.versionCount]
      }
    }

    if (keepMax === 999)
      return 0

    return keepMax - 1  // Max prunable = minimum number of versions - 1

  }
  function cacheFunc(func: string, min: number, max: number) {
    // Call rollback or prune for a single hotel/environment
    setMinVersionCount(min)
    setMaxVersionCount(max)
    setShowModal(func)
  }

  function doneCacheFunc(func: string, err: string) {
    // If error, log it (and show something)
    if (err !== "") {
      setModalText(err)
      setModalTitle(`Failed to ${func} old versions`)
      setShowModal("alert")
    } else {
      // Hide the prune/rollback modal
      setShowModal("")
      setReload(reload + 1);
    }
  }

  // #region Render

  if (props.brandCode === "" && props.hotelReferenceId === "") {
    return (
      <div style={{ padding: 20 }}>
        &lt;-- Select a brand and hotel to see the cache state for that hotel
      </div>
    )
  }

  if (props.hotelReferenceId === "") {
    return (
      <div style={{ padding: 20 }}>
        &lt;-- Select a hotel to see the cache state for that hotel
      </div>
    )
  }

  return (
    <>
      <div style={{ width: "calc(100% - 5px)" }}>
        <div className="header autoheight">{props.hotel?.name}</div>
        <table style={{ marginBottom: 10 }} className="vtable">
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold", paddingRight: 20 }}>Reference code:</td>
              <td>{props.hotel?.hotel_reference_id}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>Status:</td>
              <td>Visible in {prettyVisibility(props.hotel?.visibility ?? "")}</td>
            </tr>
          </tbody>
        </table>
        <Tabs
          justify
          defaultActiveKey={showEnvironment}
          onSelect={(eventkey) => setShowEnvironment(eventkey ?? "")}
          className="mb-3 topsep"
        >
          {
            props.runtimeEnv === "localhost"
              ? <Tab eventKey="localhost" title="Localhost">
                {renderEnvCacheTable("localhost")}
                {renderEnvDiff("localhost")}
              </Tab>
              : null
          }
          <Tab eventKey="dev" title="Dev">
            {renderEnvCacheTable("dev")}
            {renderEnvDiff("dev")}
          </Tab>
          <Tab eventKey="staging" title="Staging">
            {renderEnvCacheTable("staging")}
            {renderEnvDiff("staging")}
          </Tab>
          <Tab eventKey="live" title="Live">
            {renderEnvCacheTable("live")}
            {renderEnvDiff("live")}
          </Tab>
        </Tabs>
      </div>
      <InfoMessage title={modalTitle} text={modalText} show={showModal === "info"} onHide={() => setShowModal("")} />
      <PruneOldVersions
        hotelReferenceId={props.hotelReferenceId}
        environment={showEnvironment}
        show={showModal === "prune"}
        minPrunable={minVersionCount}
        maxPrunable={maxVersionCount}
        onHide={() => setShowModal("")}
        onDone={(err: string) => doneCacheFunc("prune", err)}
      />
      <Rollback
        hotelReferenceId={props.hotelReferenceId}
        environment={showEnvironment}
        show={showModal === "rollback"}
        maxRollback={minVersionCount}
        onHide={() => setShowModal("")}
        onDone={(err: string) => doneCacheFunc("rollback", err)}
      />
      <Alert show={showModal === "alert"} variant="danger" onClose={() => setShowModal("")} dismissible>
        <Alert.Heading>{modalTitle}</Alert.Heading>
        <p>
          {modalText}
        </p>
      </Alert>
    </>
  )
  // #endregion

}
